import { useMemo, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, Card, Divider, DropZone, InlineStack, Link, Text } from '@shopify/polaris';
import getSharePointUrlForFirnrDocumentTypeIdDocName from '../../../utils/sharepoint';
import { useLookupStore } from '../../../context/useStore.js';
import SharePointPdfPreviewButton from '../../../shared/components/SharePointPdfPreviewButton.jsx';
import { formatToCurrency } from '../../../shared/components/functions.jsx';
import { INQUIRY_STATUS_IDS, deriveInquiryStatusId, formatMoment, getInquiryStatusLabel, getInquiryStatusTone } from '../../../shared/inquiryStatus.js';

const INQUIRY_DOCUMENT_TYPE_ID = 14;

const STEPS = [
  { id: INQUIRY_STATUS_IDS.DRAFT, label: 'Draft', hint: 'Price request created' },
  { id: INQUIRY_STATUS_IDS.SENT, label: 'Sent', hint: 'Price request emailed to the supplier' },
  { id: INQUIRY_STATUS_IDS.QUOTATION_RECEIVED, label: 'Quotation received', hint: 'The supplier quotation is in' },
  { id: INQUIRY_STATUS_IDS.ORDERED, label: 'Ordered', hint: 'Purchase order made from this inquiry' }
];

function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return null;
  if (!currency || currency === 'EUR') return formatToCurrency(amount);
  return `${Number(amount).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function LifecycleSteps({ statusId, inquiry }) {
  const purchaseOrders = Array.isArray(inquiry.LinkedPurchaseOrders) ? inquiry.LinkedPurchaseOrders : [];

  const getDetail = (step) => {
    const reached = statusId >= step.id;
    if (!reached) return step.hint;
    if (step.id === INQUIRY_STATUS_IDS.DRAFT) return inquiry.Datum ? `Created ${formatMoment(String(inquiry.Datum).substring(0, 10))}` : step.hint;
    if (step.id === INQUIRY_STATUS_IDS.SENT) return inquiry.SentAt ? `Sent ${formatMoment(inquiry.SentAt)}` : 'Marked as sent';
    if (step.id === INQUIRY_STATUS_IDS.QUOTATION_RECEIVED) {
      const when = inquiry.QuotationReceivedAt ? `Received ${formatMoment(inquiry.QuotationReceivedAt)}` : 'Received';
      return inquiry.SupplierQuotationFilename ? when : `${when} (no PDF uploaded)`;
    }
    if (purchaseOrders.length === 0) return inquiry.OrderedAt ? `Ordered ${formatMoment(inquiry.OrderedAt)}` : 'Ordered';
    return (
      <InlineStack gap="200" wrap>
        {purchaseOrders.map((order) => (
          <Link key={order.id} removeUnderline url={`/purchases/${order.id}`}>
            {`PO ${order.number || order.id}${order.date ? ` (${formatMoment(order.date)})` : ''}`}
          </Link>
        ))}
      </InlineStack>
    );
  };

  return (
    <div data-testid="inquiry-lifecycle-steps">
      {STEPS.map((step, index) => {
        const done = statusId > step.id;
        const current = statusId === step.id;
        const last = index === STEPS.length - 1;
        const color = done ? '#29845a' : current ? '#2c6ecb' : '#c9cccf';
        return (
          <div key={step.id} style={{ display: 'flex', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '16px', flexShrink: 0 }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: done || current ? color : '#ffffff',
                  border: `2px solid ${color}`,
                  boxSizing: 'border-box',
                  marginTop: '4px',
                  flexShrink: 0
                }}
              />
              {!last ? <div style={{ width: '2px', flex: 1, minHeight: '16px', background: done ? '#29845a' : '#e3e3e3', margin: '3px 0' }} /> : null}
            </div>
            <div style={{ paddingBottom: last ? 0 : '10px', minWidth: 0 }}>
              <Text as="p" variant="bodyMd" fontWeight={current ? 'semibold' : 'regular'} tone={done || current ? undefined : 'subdued'}>
                {step.label}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {getDetail(step)}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuotationSummary({ summary }) {
  if (!summary) return null;
  const facts = [
    summary.quotationNumber ? `N° ${summary.quotationNumber}` : null,
    summary.totalExclVat !== null && summary.totalExclVat !== undefined ? `${formatAmount(summary.totalExclVat, summary.currency)} excl. VAT` : null,
    summary.totalInclVat !== null && summary.totalInclVat !== undefined ? `${formatAmount(summary.totalInclVat, summary.currency)} incl. VAT` : null,
    summary.validUntil ? `Valid until ${formatMoment(summary.validUntil)}` : null,
    summary.deliveryTerm ? `Delivery: ${summary.deliveryTerm}` : null
  ].filter(Boolean);

  if (facts.length === 0 && !summary.summary) return null;

  return (
    <BlockStack gap="150">
      {facts.length > 0 ? (
        <InlineStack gap="150" wrap>
          {facts.map((fact) => (
            <Badge key={fact}>{fact}</Badge>
          ))}
        </InlineStack>
      ) : null}
      {summary.summary ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {summary.summary}
        </Text>
      ) : null}
    </BlockStack>
  );
}

export default function InquiryLifecycleCard({ inquiryId, inquiry = {}, company = {}, statuses = [], disabled = false, dirty = false, onInquiryUpdated, onMakePurchaseOrder }) {
  const documenttypes = useLookupStore((state) => state.documenttypes);
  const users = useLookupStore((state) => state.users);
  const [busyAction, setBusyAction] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const isNew = inquiryId === 'new';
  const statusId = deriveInquiryStatusId(inquiry);
  const statusLabel = getInquiryStatusLabel(statusId, statuses);
  const actionsDisabled = disabled || dirty || isNew || uploading || Boolean(busyAction);

  const quotationUrl = useMemo(() => {
    if (!inquiry?.SupplierQuotationFilename || !company?.Klantnummer || !inquiry?.Prijsaanvraagnummer || !documenttypes?.length) return null;
    try {
      return getSharePointUrlForFirnrDocumentTypeIdDocName(company.Klantnummer, INQUIRY_DOCUMENT_TYPE_ID, inquiry.Prijsaanvraagnummer, inquiry.SupplierQuotationFilename, documenttypes);
    } catch (error) {
      return null;
    }
  }, [company?.Klantnummer, documenttypes, inquiry?.Prijsaanvraagnummer, inquiry?.SupplierQuotationFilename]);

  const uploadedBy = inquiry?.SupplierQuotationUploadedByID ? users?.find((user) => user.GebruikerID === inquiry.SupplierQuotationUploadedByID)?.Afkorting : null;
  const uploadedAt = inquiry?.QuotationReceivedAt || inquiry?.SupplierQuotationUploadedAt;

  const changeStatus = async (nextStatusId) => {
    if (isNew) return;
    setBusyAction(`status-${nextStatusId}`);
    setErrorMessage('');
    try {
      const response = await fetch(`/api/inquiries/${inquiryId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statusId: nextStatusId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.msg || 'Failed to change the inquiry status.');
      setParseResult(null);
      setWarnings([]);
      onInquiryUpdated(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to change the inquiry status.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDropZoneDrop = async (files) => {
    const file = files?.[0];
    if (!file || isNew) return;

    setUploading(true);
    setErrorMessage('');
    setWarnings([]);
    setParseResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/api/inquiries/${inquiryId}/supplier-quotation`, { method: 'POST', body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.msg || 'Failed to upload the supplier quotation.');

      setParseResult(data?.parseResult || null);
      setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      if (data?.inquiry) onInquiryUpdated(data.inquiry);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to upload the supplier quotation.');
    } finally {
      setUploading(false);
    }
  };

  const renderActions = () => {
    if (isNew) {
      return (
        <Text as="p" tone="subdued">
          Save the inquiry first to start its lifecycle.
        </Text>
      );
    }

    const makeOrderButton = (variant) => (
      <Button variant={variant} disabled={actionsDisabled} onClick={onMakePurchaseOrder}>
        {statusId === INQUIRY_STATUS_IDS.ORDERED ? 'Make another purchase order' : 'Make purchase order'}
      </Button>
    );

    if (statusId === INQUIRY_STATUS_IDS.DRAFT) {
      return (
        <BlockStack gap="200">
          <ButtonGroup>
            <Button variant="primary" disabled={actionsDisabled} loading={busyAction === 'status-2'} onClick={() => changeStatus(INQUIRY_STATUS_IDS.SENT)}>
              Mark as sent
            </Button>
            {makeOrderButton(undefined)}
          </ButtonGroup>
          <Text as="p" variant="bodySm" tone="subdued">
            Mailing the PDF from the document card marks the inquiry as sent automatically.
          </Text>
        </BlockStack>
      );
    }

    if (statusId === INQUIRY_STATUS_IDS.SENT) {
      return (
        <BlockStack gap="200">
          <ButtonGroup>
            <Button variant="primary" disabled={actionsDisabled} loading={busyAction === 'status-3'} onClick={() => changeStatus(INQUIRY_STATUS_IDS.QUOTATION_RECEIVED)}>
              Mark quotation received
            </Button>
            {makeOrderButton(undefined)}
            <Button variant="plain" disabled={actionsDisabled} loading={busyAction === 'status-1'} onClick={() => changeStatus(INQUIRY_STATUS_IDS.DRAFT)}>
              Back to draft
            </Button>
          </ButtonGroup>
          <Text as="p" variant="bodySm" tone="subdued">
            Upload the quotation PDF above, or mark it received when the supplier answered by phone or in an email.
          </Text>
        </BlockStack>
      );
    }

    if (statusId === INQUIRY_STATUS_IDS.QUOTATION_RECEIVED) {
      return (
        <BlockStack gap="200">
          <ButtonGroup>
            {makeOrderButton('primary')}
            <Button variant="plain" disabled={actionsDisabled} loading={busyAction === 'status-2'} onClick={() => changeStatus(INQUIRY_STATUS_IDS.SENT)}>
              Back to sent
            </Button>
          </ButtonGroup>
          <Text as="p" variant="bodySm" tone="subdued">
            Making a purchase order from this inquiry moves it to Ordered.
          </Text>
        </BlockStack>
      );
    }

    return (
      <BlockStack gap="200">
        <ButtonGroup>{makeOrderButton('plain')}</ButtonGroup>
        <Text as="p" variant="bodySm" tone="subdued">
          Ordered follows the linked purchase order; the status cannot be changed by hand.
        </Text>
      </BlockStack>
    );
  };

  return (
    <Card>
      <Box paddingBlockEnd="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h2">
            Lifecycle
          </Text>
          <Badge tone={getInquiryStatusTone(statusId)}>{statusLabel}</Badge>
        </InlineStack>
      </Box>
      <BlockStack gap="400">
        <LifecycleSteps statusId={statusId} inquiry={inquiry} />

        <Divider />

        <BlockStack gap="200">
          <Text variant="headingSm" as="h3">
            Supplier quotation
          </Text>
          {inquiry?.SupplierQuotationFilename ? (
            <BlockStack gap="150">
              <InlineStack gap="200" blockAlign="center" wrap>
                {quotationUrl ? (
                  <Link removeUnderline url={quotationUrl} target="_blank">
                    {inquiry.SupplierQuotationFilename}
                  </Link>
                ) : (
                  <Text as="span">{inquiry.SupplierQuotationFilename}</Text>
                )}
                <SharePointPdfPreviewButton
                  companyNumber={company?.Klantnummer}
                  documentTypeId={INQUIRY_DOCUMENT_TYPE_ID}
                  itemNumber={inquiry.Prijsaanvraagnummer}
                  fileName={inquiry.SupplierQuotationFilename}
                  buttonLabel="Preview"
                  modalTitle={`Supplier quotation ${inquiry.Prijsaanvraagnummer}`}
                />
              </InlineStack>
              {uploadedAt ? (
                <Text as="p" variant="bodySm" tone="subdued">
                  {`Uploaded ${formatMoment(uploadedAt)}${uploadedBy ? ` by ${uploadedBy}` : ''}`}
                </Text>
              ) : null}
              <QuotationSummary summary={inquiry.SupplierQuotationSummary} />
            </BlockStack>
          ) : (
            <Text as="p" tone="subdued">
              {isNew ? 'Upload becomes available once the inquiry has been saved.' : 'No supplier quotation uploaded yet.'}
            </Text>
          )}

          {parseResult ? (
            <Banner
              tone={parseResult.summary || parseResult.quotationNumber || parseResult.totalExclVat !== null ? 'success' : 'warning'}
              title={parseResult.summary || parseResult.quotationNumber ? 'Quotation read' : 'Quotation stored'}
            >
              <p>{parseResult.notes}</p>
            </Banner>
          ) : null}

          {warnings.length > 0 ? (
            <Banner tone="warning" title="Upload warning">
              {warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </Banner>
          ) : null}

          {errorMessage ? (
            <Banner tone="critical" title="Something went wrong">
              <p>{errorMessage}</p>
            </Banner>
          ) : null}

          {!isNew ? (
            <DropZone allowMultiple={false} accept=".pdf,application/pdf" onDrop={handleDropZoneDrop} disabled={uploading || disabled || dirty}>
              <DropZone.FileUpload
                actionTitle={uploading ? 'Uploading quotation...' : inquiry?.SupplierQuotationFilename ? 'Upload a newer quotation PDF' : 'Upload supplier quotation PDF'}
                actionHint="Stored with the price request on SharePoint and marks the inquiry as received. Number, amount and validity are read automatically."
              />
            </DropZone>
          ) : null}
        </BlockStack>

        <Divider />

        {dirty && !isNew ? (
          <Text as="p" variant="bodySm" tone="subdued">
            Save or discard your changes first to use the lifecycle actions.
          </Text>
        ) : null}
        {renderActions()}
      </BlockStack>
    </Card>
  );
}
